import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { 
  Calendar, Download, Loader2, Users, Settings, Heart, UserCheck, Network,
  CheckCircle, AlertTriangle, TrendingUp, Target, Star, ArrowRight, Building2,
  Zap, Shield, BarChart3, Award
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/k6trts4z_Bitmap%20blanc.png';

// Dimension configuration with icons and colors
const DIMENSIONS = {
  team_resources: { 
    name: 'Team Resources', 
    nameFR: 'Ressources', 
    impact: 'Delivery Capacity',
    impactFR: 'Capacité de Livraison',
    icon: Users, 
    color: '#3B82F6',
    description: 'Skills, bandwidth, and resource allocation',
    descriptionFR: 'Compétences, disponibilité et allocation des ressources'
  },
  ways_of_working: { 
    name: 'Ways of Working', 
    nameFR: 'Organisation', 
    impact: 'Execution Excellence',
    impactFR: 'Excellence Opérationnelle',
    icon: Settings, 
    color: '#8B5CF6',
    description: 'Processes, tools, and collaboration patterns',
    descriptionFR: 'Processus, outils et modes de collaboration'
  },
  team_engagement: { 
    name: 'Team Engagement', 
    nameFR: 'Engagement', 
    impact: 'Sustainable Performance',
    impactFR: 'Performance Durable',
    icon: Heart, 
    color: '#EC4899',
    description: 'Motivation, trust, and psychological safety',
    descriptionFR: 'Motivation, confiance et sécurité psychologique'
  },
  manager_posture: { 
    name: 'Manager Posture', 
    nameFR: 'Posture Manager', 
    impact: 'Leadership Enablement',
    impactFR: 'Enablement Leadership',
    icon: UserCheck, 
    color: '#F59E0B',
    description: 'Leadership style and team empowerment',
    descriptionFR: 'Style de leadership et autonomisation'
  },
  ecosystem: { 
    name: 'Ecosystem', 
    nameFR: 'Écosystème', 
    impact: 'Organizational Alignment',
    impactFR: 'Alignement Organisationnel',
    icon: Network, 
    color: '#10B981',
    description: 'Cross-team collaboration and stakeholder relations',
    descriptionFR: 'Collaboration inter-équipes et relations parties prenantes'
  }
};

// Maturity configuration with interpretations
const MATURITY_CONFIG = {
  fragile: { 
    label: 'Fragile', 
    labelFR: 'Fragile', 
    color: 'border-red-500/50 text-red-400 bg-red-500/10',
    dotColor: 'bg-red-500',
    interpretation: {
      en: 'Your team is in a critical state requiring immediate intervention. Performance risks are high and sustainable delivery is compromised.',
      fr: 'Votre équipe est dans un état critique nécessitant une intervention immédiate. Les risques de performance sont élevés.'
    },
    implications: {
      en: ['High turnover risk', 'Delivery delays likely', 'Team morale at risk'],
      fr: ['Risque élevé de turnover', 'Retards de livraison probables', 'Moral de l\'équipe à risque']
    },
    nextLevel: { en: 'Developing', fr: 'En développement' }
  },
  developing: { 
    label: 'Developing', 
    labelFR: 'En développement', 
    color: 'border-orange-500/50 text-orange-400 bg-orange-500/10',
    dotColor: 'bg-orange-500',
    interpretation: {
      en: 'Your team is building foundations for improvement. Key processes are being established but consistency is not yet achieved.',
      fr: 'Votre équipe construit les fondations pour s\'améliorer. Les processus clés s\'établissent mais la cohérence n\'est pas encore atteinte.'
    },
    implications: {
      en: ['Performance variability', 'Growth potential identified', 'Investment in structure needed'],
      fr: ['Variabilité de la performance', 'Potentiel de croissance identifié', 'Investissement structurel nécessaire']
    },
    nextLevel: { en: 'Performing', fr: 'Performant' }
  },
  performing: { 
    label: 'Performing', 
    labelFR: 'Performant', 
    color: 'border-yellow-500/50 text-yellow-400 bg-yellow-500/10',
    dotColor: 'bg-yellow-500',
    interpretation: {
      en: 'Your team delivers consistent results with room for optimization. The next step is to move toward Sustainable High Performance.',
      fr: 'Votre équipe livre des résultats constants avec des opportunités d\'optimisation. La prochaine étape est la Haute Performance Durable.'
    },
    implications: {
      en: ['Reliable delivery', 'Optimization opportunities exist', 'Ready for excellence push'],
      fr: ['Livraison fiable', 'Opportunités d\'optimisation', 'Prêt pour l\'excellence']
    },
    nextLevel: { en: 'High Performing', fr: 'Haute Performance' }
  },
  high_performing: { 
    label: 'High Performing', 
    labelFR: 'Haute Performance', 
    color: 'border-green-500/50 text-green-400 bg-green-500/10',
    dotColor: 'bg-green-500',
    interpretation: {
      en: 'Your team operates at an elite level with excellent execution across all dimensions. Focus on maintaining momentum and sharing best practices.',
      fr: 'Votre équipe opère à un niveau élite avec une excellente exécution. Focus sur le maintien de la dynamique et le partage des meilleures pratiques.'
    },
    implications: {
      en: ['Competitive advantage', 'Role model for organization', 'Innovation capacity'],
      fr: ['Avantage compétitif', 'Modèle pour l\'organisation', 'Capacité d\'innovation']
    },
    nextLevel: null
  }
};

// Service recommendations with value propositions
const SERVICE_VALUE_PROPS = {
  'Agile Recruitment': {
    en: 'Build the right team with precision hiring aligned to your culture and goals.',
    fr: 'Construisez la bonne équipe avec un recrutement précis aligné sur votre culture et vos objectifs.'
  },
  'Team Dynamic Enhancement': {
    en: 'Unlock your team\'s potential through targeted interventions and coaching.',
    fr: 'Libérez le potentiel de votre équipe grâce à des interventions ciblées et du coaching.'
  },
  'Leadership Support': {
    en: 'Elevate your leaders with executive coaching and strategic alignment.',
    fr: 'Élevez vos leaders avec du coaching exécutif et un alignement stratégique.'
  },
  'Leadership & Transformation Advisory': {
    en: 'Transform your organization with strategic leadership and change management.',
    fr: 'Transformez votre organisation avec un leadership stratégique et la gestion du changement.'
  }
};

const ResultsPage = () => {
  const { language } = useLanguage();
  const { id } = useParams();
  const location = useLocation();
  const [results, setResults] = useState(location.state?.results || null);
  const [loading, setLoading] = useState(!location.state?.results);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!results && id) {
      fetchResults();
    }
  }, [id, results]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/diagnosis/results/${id}`);
      setResults(response.data);
    } catch (err) {
      setError('Unable to load results.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!id || downloadingPdf) return;
    
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/diagnosis/pdf/${id}?lang=${language}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TeamCraft_Report_${results?.company?.replace(/\s+/g, '_') || 'Report'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'fr' ? 'PDF téléchargé avec succès' : 'PDF downloaded successfully');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error(language === 'fr' ? 'Erreur lors du téléchargement du PDF' : 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#C5A059] mx-auto mb-4" />
          <p className="text-gray-400 font-medium tracking-wide">
            {language === 'fr' ? 'Chargement du rapport...' : 'Loading report...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A]">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">{error || 'Results not found'}</p>
          <Link to="/diagnosis">
            <Button className="bg-[#C5A059] text-[#1A1A1A] hover:bg-[#D4AF61]">
              {language === 'fr' ? 'Nouvelle Évaluation' : 'Start New Assessment'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const dimensionScores = results.dimension_scores || {};
  const maturity = MATURITY_CONFIG[results.maturity_level] || MATURITY_CONFIG.developing;
  
  // Prepare radar chart data
  const radarData = Object.entries(dimensionScores).map(([key, value]) => ({
    dimension: language === 'fr' ? DIMENSIONS[key]?.nameFR : DIMENSIONS[key]?.name || key,
    score: value || 0,
    fullMark: 5
  }));

  // Calculate overall score
  const dimensionValues = Object.values(dimensionScores);
  const avgScore = dimensionValues.length > 0 
    ? dimensionValues.reduce((a, b) => a + b, 0) / dimensionValues.length 
    : (results.total_score ? results.total_score / 5 : 0);
  const scorePercentage = (avgScore / 5) * 100;
  const totalScore = results.total_score || (avgScore * 5);

  // Sort dimensions by score for breakdown
  const sortedDimensions = Object.entries(dimensionScores)
    .map(([key, value]) => ({ key, value, ...DIMENSIONS[key] }))
    .sort((a, b) => a.value - b.value);

  // Get service value prop
  const serviceValueProp = SERVICE_VALUE_PROPS[results.recommended_service] || SERVICE_VALUE_PROPS['Team Dynamic Enhancement'];

  return (
    <div className="min-h-screen bg-[#1A1A1A] pt-20" data-testid="results-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#1A1A1A]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-6">
              <img src={LOGO_URL} alt="TeamCraft" className="h-12 w-auto" />
              <div className="h-8 w-px bg-white/10 hidden md:block" />
              <div>
                <p className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase">
                  {language === 'fr' ? 'Diagnostic de Performance' : 'Performance Diagnosis'}
                </p>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#C5A059]" />
                  {results.company || 'Organization'}
                </h1>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase">
                {language === 'fr' ? 'Date du Rapport' : 'Report Date'}
              </p>
              <p className="text-white font-medium">
                {new Date(results.created_at || Date.now()).toLocaleDateString(
                  language === 'fr' ? 'fr-FR' : 'en-US', 
                  { year: 'numeric', month: 'long', day: 'numeric' }
                )}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Bento Grid */}
        <section className="grid grid-cols-12 gap-4 mb-8">
          {/* Radar Chart - Main Hero */}
          <div className="col-span-12 lg:col-span-6 row-span-2 bg-[#222222] border border-white/5 p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase mb-1">
                  {language === 'fr' ? 'Vue d\'Ensemble' : 'Performance Overview'}
                </p>
                <h2 className="text-xl font-bold text-white">TeamCraft Framework</h2>
              </div>
              <BarChart3 className="w-6 h-6 text-[#C5A059]" />
            </div>
            <div className="h-[320px] lg:h-[380px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                  <PolarGrid stroke="#404040" strokeWidth={1} />
                  <PolarAngleAxis 
                    dataKey="dimension" 
                    tick={{ fill: '#A3A3A3', fontSize: 11, fontWeight: 500 }} 
                    tickLine={false}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 5]} 
                    tick={{ fill: '#525252', fontSize: 10 }} 
                    tickCount={6}
                    axisLine={false}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#C5A059"
                    fill="#C5A059"
                    fillOpacity={0.2}
                    strokeWidth={2}
                    dot={{ fill: '#C5A059', strokeWidth: 0, r: 4 }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {/* Target benchmark line */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-white/5">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#C5A059] rounded-full" />
                <span className="text-xs text-gray-400">{language === 'fr' ? 'Votre Score' : 'Your Score'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-0.5 bg-green-500" />
                <span className="text-xs text-gray-400">{language === 'fr' ? 'Cible Haute Performance: 4.0+' : 'High Performance Target: 4.0+'}</span>
              </div>
            </div>
          </div>

          {/* Overall Score */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3 bg-[#222222] border border-white/5 p-6">
            <p className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase mb-4">
              {language === 'fr' ? 'Score Global' : 'Overall Score'}
            </p>
            <div className="flex items-center gap-4">
              {/* Circular Progress */}
              <div className="relative w-24 h-24 flex-shrink-0">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle 
                    cx="48" cy="48" r="42" 
                    stroke="#404040" 
                    strokeWidth="6" 
                    fill="none" 
                  />
                  <circle 
                    cx="48" cy="48" r="42" 
                    stroke="url(#bronzeGradient)" 
                    strokeWidth="6" 
                    fill="none"
                    strokeDasharray={`${scorePercentage * 2.64} 264`}
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-out"
                  />
                  <defs>
                    <linearGradient id="bronzeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#C5A059" />
                      <stop offset="100%" stopColor="#8A703E" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{avgScore.toFixed(1)}</span>
                </div>
              </div>
              <div>
                <p className="text-4xl font-light tracking-wide text-[#C5A059]">
                  {totalScore.toFixed(1)}
                  <span className="text-lg text-gray-500">/25</span>
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {language === 'fr' ? 'Score Total' : 'Total Score'}
                </p>
              </div>
            </div>
          </div>

          {/* Maturity Level */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3 bg-[#222222] border border-white/5 p-6">
            <p className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase mb-4">
              {language === 'fr' ? 'Niveau de Maturité' : 'Maturity Level'}
            </p>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-3 h-3 rounded-full ${maturity.dotColor}`} />
              <span className={`px-3 py-1 border text-sm font-bold tracking-wide ${maturity.color}`}>
                {language === 'fr' ? maturity.labelFR : maturity.label}
              </span>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              {language === 'fr' ? maturity.interpretation.fr : maturity.interpretation.en}
            </p>
            {maturity.nextLevel && (
              <p className="text-xs text-[#C5A059] mt-3 flex items-center gap-1">
                <ArrowRight className="w-3 h-3" />
                {language === 'fr' ? 'Prochaine étape' : 'Next level'}: {language === 'fr' ? maturity.nextLevel.fr : maturity.nextLevel.en}
              </p>
            )}
          </div>

          {/* Key Insight */}
          <div className="col-span-12 lg:col-span-6 bg-gradient-to-r from-[#C5A059]/10 to-[#222222] border border-[#C5A059]/30 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#C5A059]" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-[0.2em] text-[#C5A059] uppercase mb-2">
                  {language === 'fr' ? 'Recommandation Prioritaire' : 'Priority Recommendation'}
                </p>
                <p className="text-white font-medium mb-2">{results.recommended_service}</p>
                <p className="text-sm text-gray-300">
                  {language === 'fr' ? serviceValueProp.fr : serviceValueProp.en}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Business Implications */}
        <section className="mb-8 bg-[#222222] border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#C5A059]" />
            <h3 className="text-lg font-bold text-white">
              {language === 'fr' ? 'Implications Business' : 'Business Implications'}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(language === 'fr' ? maturity.implications.fr : maturity.implications.en).map((implication, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-[#1A1A1A] border border-white/5">
                <div className="w-6 h-6 bg-[#C5A059]/20 flex items-center justify-center text-xs font-bold text-[#C5A059]">
                  {idx + 1}
                </div>
                <span className="text-sm text-gray-300">{implication}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Dimension Breakdown */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C5A059]" />
              {language === 'fr' ? 'Analyse par Dimension' : 'Dimension Analysis'}
            </h3>
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              {language === 'fr' ? 'Trié par priorité' : 'Sorted by priority'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {sortedDimensions.map(({ key, value, name, nameFR, impact, impactFR, icon: Icon, color }) => {
              const percentage = (value / 5) * 100;
              const isLow = value < 3;
              return (
                <div 
                  key={key} 
                  className={`bg-[#222222] border p-5 transition-all hover:border-[#C5A059]/50 ${
                    isLow ? 'border-orange-500/30' : 'border-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Icon className="w-5 h-5" style={{ color }} />
                    {isLow && (
                      <span className="text-[10px] font-bold tracking-wider text-orange-400 uppercase bg-orange-500/10 px-2 py-0.5">
                        {language === 'fr' ? 'Focus' : 'Focus'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-bold text-white mb-1">
                    {language === 'fr' ? nameFR : name}
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    → {language === 'fr' ? impactFR : impact}
                  </p>
                  <div className="flex items-end justify-between">
                    <span className="text-2xl font-light text-[#C5A059]">{value?.toFixed(1) || '0.0'}</span>
                    <span className="text-xs text-gray-500">/5</span>
                  </div>
                  <div className="mt-2 h-1 bg-[#404040] overflow-hidden">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ width: `${percentage}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Strengths & Focus Areas */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {/* Strengths */}
          <div className="bg-[#222222] border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {language === 'fr' ? 'Points Forts à Capitaliser' : 'Strengths to Leverage'}
                </h3>
                <p className="text-xs text-gray-500">{language === 'fr' ? 'Vos avantages compétitifs' : 'Your competitive advantages'}</p>
              </div>
            </div>
            <div className="space-y-3">
              {(results.strengths || []).map((strength, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-green-500/5 border border-green-500/10">
                  <Star className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">{strength}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Focus Areas */}
          <div className="bg-[#222222] border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 bg-orange-500/20 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {language === 'fr' ? 'Axes de Développement' : 'Focus Areas'}
                </h3>
                <p className="text-xs text-gray-500">{language === 'fr' ? 'Opportunités d\'amélioration' : 'Improvement opportunities'}</p>
              </div>
            </div>
            <div className="space-y-3">
              {(results.focus_areas || []).map((area, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-orange-500/5 border border-orange-500/10">
                  <Target className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Conversion Footer / CTA */}
        <section className="bg-gradient-to-r from-[#1A1A1A] via-[#222222] to-[#1A1A1A] border border-[#C5A059]/30 p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Award className="w-6 h-6 text-[#C5A059]" />
                <p className="text-xs font-bold tracking-[0.2em] text-[#C5A059] uppercase">
                  {language === 'fr' ? 'Prochaine Étape' : 'Next Step'}
                </p>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                {language === 'fr' 
                  ? 'Transformez ces insights en résultats concrets'
                  : 'Transform these insights into concrete results'}
              </h2>
              <p className="text-gray-300 max-w-xl">
                {language === 'fr'
                  ? 'Réservez un appel découverte gratuit de 30 minutes avec nos experts pour élaborer votre plan d\'action personnalisé.'
                  : 'Book a free 30-minute discovery call with our experts to develop your personalized action plan.'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={`/booking/${id}`}>
                <Button 
                  className="bg-[#C5A059] hover:bg-[#D4AF61] text-[#1A1A1A] font-bold uppercase tracking-widest px-8 py-6 text-sm w-full sm:w-auto"
                  data-testid="final-cta-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Réserver un Appel' : 'Book a Call'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:border-[#C5A059] hover:text-[#C5A059] font-bold uppercase tracking-widest px-6 py-6 text-sm bg-transparent"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {downloadingPdf 
                  ? (language === 'fr' ? 'Génération...' : 'Generating...') 
                  : (language === 'fr' ? 'Télécharger PDF' : 'Download PDF')}
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="TeamCraft" className="h-8 w-auto opacity-60" />
              <p className="text-sm text-gray-500">
                {language === 'fr' 
                  ? 'Performance d\'Équipe & Transformation Organisationnelle'
                  : 'Team Performance & Organizational Transformation'}
              </p>
            </div>
            <p className="text-sm text-gray-500">
              contact@teamcraft.lu
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResultsPage;
